import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Alert, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material'
import { CheckCircleOutline, HighlightOff, TextSnippetOutlined } from '@mui/icons-material'
import { apiPost } from '../generic/Api_Functions'
import { useSmartTranslation } from '../generic/hooks/useSmartTranslation'
import { SmartDialog } from '../generic/utilities/SmartDialog'
import { useAuthorization } from '../generic/hooks/useAuthorization'
import Document from '../configuration/document/Document'

/**
 * Execution screen for a quality incidence action. Lets the operator add an execution comment,
 * consult/attach documents and set the execution outcome: OK (conforming) or NO_OK (non-conforming).
 * Either outcome marks the action done. Reused from both the Quality module actions tables and the
 * per-incidence actions table.
 */
const ActionExecutionDialog = ({ action, onClose, onExecuted }) => {
  const { t_ } = useSmartTranslation()
  const isUserAuthorized = useAuthorization()
  const [comment, setComment] = useState(action?.executionComment || '')
  const [showDocuments, setShowDocuments] = useState(false)
  const [isDownloadingFile, setIsDownloadingFile] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')

  const actionUuid = action?.uuid || action?.qualityIncidenceActionUuid
  const currentStatus = String(action?.actionStatus || '').trim().toUpperCase()
  const currentResult = String(action?.actionResult || '').trim().toUpperCase()

  const execute = async (result) => {
    if (!actionUuid || submitting) return
    setSubmitting(true)
    setError('')
    try {
      await apiPost('executeQualityIncidenceAction/' + actionUuid, { executionComment: comment, result })
      onExecuted?.()
      onClose?.()
    } catch (err) {
      setError(err?.message || t_('Error executing the action'))
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Box sx={{ p: 2, minWidth: { xs: 280, sm: 460 }, textAlign: 'left' }}>
      <Stack direction='row' spacing={1} alignItems='center' sx={{ mb: 2, flexWrap: 'wrap' }}>
        <Typography variant='h6' sx={{ fontWeight: 700 }}>
          {action?.code ? `${t_('Execute action')} · ${action.code}` : t_('Execute action')}
        </Typography>
        {currentStatus && <Chip size='small' label={t_(currentStatus)} />}
        {(currentResult === 'OK' || currentResult === 'NO_OK') && (
          <Chip
            size='small'
            color={currentResult === 'NO_OK' ? 'error' : 'success'}
            label={currentResult}
          />
        )}
      </Stack>

      {error && (
        <Alert severity='error' sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <TextField
        label={t_('Execution comment')}
        value={comment}
        onChange={(event) => setComment(event.target.value)}
        multiline
        minRows={4}
        fullWidth
      />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 2 }} justifyContent='space-between' alignItems={{ sm: 'center' }}>
        <Button
          startIcon={<TextSnippetOutlined />}
          onClick={() => setShowDocuments(true)}
          disabled={!isUserAuthorized('DOCUMENTS_QUALITY_INCIDENCE') || !actionUuid}
        >
          {t_('Documents')}
        </Button>
        <Stack direction='row' spacing={1}>
          <Button
            color='error'
            variant='outlined'
            startIcon={<HighlightOff />}
            disabled={submitting || !actionUuid}
            onClick={() => execute('NO_OK')}
          >
            NO OK
          </Button>
          <Button
            color='success'
            variant='contained'
            startIcon={<CheckCircleOutline />}
            disabled={submitting || !actionUuid}
            onClick={() => execute('OK')}
          >
            OK
          </Button>
        </Stack>
      </Stack>

      <SmartDialog
        setOpen={showDocuments}
        closeCallback={() => setShowDocuments(false)}
        accept={false}
        cancel={false}
        close
        disableButtons={isDownloadingFile}
        renderComponent={
          <Document
            reference={{ id: actionUuid, code: action?.code }}
            resourceType='QUALITY_INCIDENCE_ACTION'
            isDownloadingFile={isDownloadingFile}
            setIsDownloadingFile={setIsDownloadingFile}
            defaultName={action?.code}
          />
        }
      />
    </Box>
  )
}

ActionExecutionDialog.propTypes = {
  action: PropTypes.object,
  onClose: PropTypes.func,
  onExecuted: PropTypes.func
}

export default ActionExecutionDialog
